import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Divider,
  CircularProgress,
  Pagination,
  Menu,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  PendingActions as PendingIcon,
} from '@mui/icons-material';
import savingsService from '../services/savingsService';
import { useToast } from '../contexts/ToastContext';

const SavingsManagement = () => {
  const { showToast } = useToast();
  const [savings, setSavings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedSavings, setSelectedSavings] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [maturityDialogOpen, setMaturityDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statsPeriod, setStatsPeriod] = useState('month');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSavings, setMenuSavings] = useState(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [withdrawalsPage, setWithdrawalsPage] = useState(1);
  const [withdrawalsTotalPages, setWithdrawalsTotalPages] = useState(1);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  
  // Auto-withdrawal settings states
  const [autoWithdrawalSettings, setAutoWithdrawalSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    spendSave: {
      enabled: false,
      minimumBalance: 0,
      withdrawalAmount: 0,
      withdrawalPercentage: 0,
      destination: 'flex',
    },
    receiveSave: {
      enabled: false,
      minimumBalance: 0,
      withdrawalAmount: 0,
      withdrawalPercentage: 0,
      destination: 'flex',
    },
  });

  // Savings Types states
  const [savingsTypes, setSavingsTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [updatingType, setUpdatingType] = useState(null);

  // Form states
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    action: 'add',
    description: '',
  });
  const [statusForm, setStatusForm] = useState({
    status: 'active',
  });
  const [maturityForm, setMaturityForm] = useState({
    action: 'mature',
    newMaturityDate: '',
  });

  useEffect(() => {
    fetchSavings();
    fetchStats();
    fetchPendingWithdrawals();
    if (currentTab === 8) {
      fetchAutoWithdrawalSettings();
    } else if (currentTab === 9) {
      fetchSavingsTypes();
    }
  }, [page, typeFilter, statusFilter, searchTerm, statsPeriod, withdrawalsPage, currentTab]);

  const fetchSavings = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: '20',
      };
      
      if (searchTerm) params.search = searchTerm;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await savingsService.getAllSavings(params);
      setSavings(response.data.savings || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching savings:', error);
      showSnackbar('Failed to fetch savings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await savingsService.getSavingsStats(statsPeriod);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPendingWithdrawals = async () => {
    try {
      const params = {
        page: withdrawalsPage.toString(),
        limit: '20',
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await savingsService.getPendingWithdrawals(params);
      setPendingWithdrawals(response.data.withdrawals || []);
      setWithdrawalsTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching pending withdrawals:', error);
    }
  };

  const fetchAutoWithdrawalSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await savingsService.getAutoWithdrawalSettings();
      if (response.status && response.data) {
        setAutoWithdrawalSettings(response.data);
        setSettingsForm({
          spendSave: {
            enabled: response.data.spendSaveAutoWithdrawal?.enabled || false,
            minimumBalance: response.data.spendSaveAutoWithdrawal?.minimumBalance || 0,
            withdrawalAmount: response.data.spendSaveAutoWithdrawal?.withdrawalAmount || 0,
            withdrawalPercentage: response.data.spendSaveAutoWithdrawal?.withdrawalPercentage || 0,
            destination: response.data.spendSaveAutoWithdrawal?.destination || 'flex',
          },
          receiveSave: {
            enabled: response.data.receiveSaveAutoWithdrawal?.enabled || false,
            minimumBalance: response.data.receiveSaveAutoWithdrawal?.minimumBalance || 0,
            withdrawalAmount: response.data.receiveSaveAutoWithdrawal?.withdrawalAmount || 0,
            withdrawalPercentage: response.data.receiveSaveAutoWithdrawal?.withdrawalPercentage || 0,
            destination: response.data.receiveSaveAutoWithdrawal?.destination || 'flex',
          },
        });
      }
    } catch (error) {
      console.error('Error fetching auto-withdrawal settings:', error);
      showSnackbar('Failed to load auto-withdrawal settings', 'error');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveAutoWithdrawalSettings = async () => {
    try {
      setSavingSettings(true);
      const response = await savingsService.updateAutoWithdrawalSettings({
        spendSaveAutoWithdrawal: settingsForm.spendSave,
        receiveSaveAutoWithdrawal: settingsForm.receiveSave,
      });
      if (response.status) {
        showSnackbar('Auto-withdrawal settings updated successfully', 'success');
        await fetchAutoWithdrawalSettings();
      }
    } catch (error) {
      console.error('Error saving auto-withdrawal settings:', error);
      showSnackbar(error.message || 'Failed to save auto-withdrawal settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchSavingsTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await savingsService.getSavingsTypes();
      if (response.status && response.data) {
        setSavingsTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching savings types:', error);
      showSnackbar('Failed to load savings types', 'error');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleToggleSavingsType = async (type, enabled) => {
    try {
      setUpdatingType(type);
      const response = await savingsService.updateSavingsType(type, { enabled });
      if (response.status) {
        showSnackbar(`Savings type ${enabled ? 'enabled' : 'disabled'} successfully`, 'success');
        await fetchSavingsTypes();
      }
    } catch (error) {
      console.error('Error updating savings type:', error);
      showSnackbar('Failed to update savings type', 'error');
    } finally {
      setUpdatingType(null);
    }
  };

  const handleUpdateROI = async (type, roi) => {
    try {
      setUpdatingType(type);
      const roiValue = parseFloat(roi);
      if (isNaN(roiValue) || roiValue < 0 || roiValue > 100) {
        showSnackbar('ROI must be a number between 0 and 100', 'error');
        return;
      }
      const response = await savingsService.updateSavingsType(type, { roi: roiValue });
      if (response.status) {
        showSnackbar('ROI updated successfully', 'success');
        await fetchSavingsTypes();
      }
    } catch (error) {
      console.error('Error updating ROI:', error);
      showSnackbar('Failed to update ROI', 'error');
    } finally {
      setUpdatingType(null);
    }
  };

  const handleApproveWithdrawal = async (withdrawal) => {
    try {
      await savingsService.approveWithdrawal(withdrawal._id);
      showSnackbar('Withdrawal approved successfully', 'success');
      fetchPendingWithdrawals();
      fetchSavings();
      fetchStats();
    } catch (error) {
      showSnackbar('Failed to approve withdrawal: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleRejectWithdrawal = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      await savingsService.rejectWithdrawal(selectedWithdrawal._id, rejectReason);
      showSnackbar('Withdrawal rejected successfully', 'success');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedWithdrawal(null);
      fetchPendingWithdrawals();
    } catch (error) {
      showSnackbar('Failed to reject withdrawal: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    showToast(message, severity);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewSavings = async (saving) => {
    try {
      const response = await savingsService.getSavingsById(saving._id);
      setSelectedSavings(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      showSnackbar('Failed to fetch savings details', 'error');
    }
  };

  const handleBalanceAdjustment = (saving) => {
    setSelectedSavings(saving);
    setBalanceForm({
      amount: '',
      action: 'add',
      description: '',
    });
    setBalanceDialogOpen(true);
    setAnchorEl(null);
  };

  const handleStatusUpdate = (saving) => {
    setSelectedSavings(saving);
    setStatusForm({
      status: saving.status,
    });
    setStatusDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMaturityUpdate = (saving) => {
    if (saving.type !== 'fixed') return;
    setSelectedSavings(saving);
    setMaturityForm({
      action: 'mature',
      newMaturityDate: '',
    });
    setMaturityDialogOpen(true);
    setAnchorEl(null);
  };

  const handleBalanceSubmit = async () => {
    try {
      if (!balanceForm.amount || balanceForm.amount <= 0) {
        showSnackbar('Please enter a valid amount', 'error');
        return;
      }

      await savingsService.updateSavingsBalance(selectedSavings._id, {
        amount: parseFloat(balanceForm.amount),
        action: balanceForm.action,
        description: balanceForm.description,
      });

      showSnackbar('Balance updated successfully', 'success');
      setBalanceDialogOpen(false);
      fetchSavings();
      fetchStats();
    } catch (error) {
      showSnackbar('Failed to update balance: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleStatusSubmit = async () => {
    try {
      await savingsService.updateSavingsStatus(selectedSavings._id, statusForm.status);
      showSnackbar('Status updated successfully', 'success');
      setStatusDialogOpen(false);
      fetchSavings();
    } catch (error) {
      showSnackbar('Failed to update status: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleMaturitySubmit = async () => {
    try {
      const data = {
        action: maturityForm.action,
      };
      
      if (maturityForm.action === 'extend' && !maturityForm.newMaturityDate) {
        showSnackbar('Please select a new maturity date', 'error');
        return;
      }
      
      if (maturityForm.action === 'extend') {
        data.newMaturityDate = maturityForm.newMaturityDate;
      }

      await savingsService.updateFixedSavingsMaturity(selectedSavings._id, data);
      showSnackbar('Fixed savings updated successfully', 'success');
      setMaturityDialogOpen(false);
      fetchSavings();
      fetchStats();
    } catch (error) {
      showSnackbar('Failed to update maturity: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleDelete = async (saving) => {
    if (!window.confirm('Are you sure you want to delete this savings? This action cannot be undone.')) {
      return;
    }

    try {
      await savingsService.deleteSavings(saving._id);
      showSnackbar('Savings deleted successfully', 'success');
      fetchSavings();
      fetchStats();
      setAnchorEl(null);
    } catch (error) {
      showSnackbar('Failed to delete: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleMenuOpen = (event, saving) => {
    setAnchorEl(event.currentTarget);
    setMenuSavings(saving);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSavings(null);
  };

  const getSavingsTypeLabel = (type) => {
    const labels = {
      'flex': 'Flex',
      'las': 'LAS',
      'vault': 'Vault',
      'fixed': 'Fixed',
      'spend-save': 'Spend & Save',
      'receive-save': 'Receive & Save',
    };
    return labels[type] || type;
  };

  const getSavingsBalance = (saving) => {
    switch (saving.type) {
      case 'flex':
        return saving.flexBalance || 0;
      case 'las':
        return saving.lasCurrentSavings || 0;
      case 'vault':
        return saving.vaultBalance || 0;
      case 'fixed':
        return saving.fixedAmount || 0;
      case 'spend-save':
        return saving.spendSaveBalance || 0;
      case 'receive-save':
        return saving.receiveSaveBalance || 0;
      default:
        return 0;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'inactive': 'default',
      'completed': 'info',
      'cancelled': 'error',
      'matured': 'success',
    };
    return colors[status] || 'default';
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const filteredSavings = savings.filter(saving => {
    const matchesSearch = !searchTerm || 
      saving.userId?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      saving.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      saving.userId?.fullname?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const tabCounts = {
    all: savings.length,
    flex: savings.filter(s => s.type === 'flex').length,
    las: savings.filter(s => s.type === 'las').length,
    vault: savings.filter(s => s.type === 'vault').length,
    fixed: savings.filter(s => s.type === 'fixed').length,
    'spend-save': savings.filter(s => s.type === 'spend-save').length,
    'receive-save': savings.filter(s => s.type === 'receive-save').length,
  };

  if (loading && savings.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Savings Management
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Monitor and manage all user savings
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <WalletIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{formatCurrency(stats.totals?.grandTotal || 0)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Savings
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{formatCurrency(stats.transactions?.totalDeposits || 0)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Deposits
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <RemoveIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{formatCurrency(stats.transactions?.totalWithdrawals || 0)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Withdrawals
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{formatCurrency(stats.transactions?.totalInterest || 0)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Interest
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by username, email, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type Filter</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Type Filter"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="flex">Flex</MenuItem>
                  <MenuItem value="las">LAS</MenuItem>
                  <MenuItem value="vault">Vault</MenuItem>
                  <MenuItem value="fixed">Fixed</MenuItem>
                  <MenuItem value="spend-save">Spend & Save</MenuItem>
                  <MenuItem value="receive-save">Receive & Save</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchSavings();
                  fetchStats();
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label={`All (${tabCounts.all})`} />
          <Tab label={`Flex (${tabCounts.flex})`} />
          <Tab label={`LAS (${tabCounts.las})`} />
          <Tab label={`Vault (${tabCounts.vault})`} />
          <Tab label={`Fixed (${tabCounts.fixed})`} />
          <Tab label={`Spend & Save (${tabCounts['spend-save']})`} />
          <Tab label={`Receive & Save (${tabCounts['receive-save']})`} />
          <Tab 
            label={
              <Badge badgeContent={pendingWithdrawals.length} color="error">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PendingIcon /> Pending Withdrawals
                </Box>
              </Badge>
            } 
          />
          <Tab label="Auto-Withdrawal Settings" />
          <Tab label="Savings Types" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSavings.map((saving) => (
                  <TableRow key={saving._id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {saving.userId?.fullname || saving.userId?.username || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {saving.userId?.email || ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={getSavingsTypeLabel(saving.type)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(getSavingsBalance(saving))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={saving.status} 
                        color={getStatusColor(saving.status)}
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(saving.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, saving)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </TabPanel>

        {/* Pending Withdrawals Tab */}
        <TabPanel value={currentTab} index={7}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingWithdrawals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No pending withdrawals
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingWithdrawals.map((withdrawal) => (
                    <TableRow key={withdrawal._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {withdrawal.userId?.fullname || withdrawal.userId?.username || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {withdrawal.userId?.email || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={getSavingsTypeLabel(withdrawal.savingsType)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(withdrawal.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {withdrawal.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(withdrawal.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApproveWithdrawal(withdrawal)}
                            sx={{ mr: 1 }}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedWithdrawal(withdrawal);
                              setRejectDialogOpen(true);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {withdrawalsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={withdrawalsTotalPages}
                page={withdrawalsPage}
                onChange={(e, value) => setWithdrawalsPage(value)}
                color="primary"
              />
            </Box>
          )}
        </TabPanel>

        {/* Auto-Withdrawal Settings Tab */}
        <TabPanel value={currentTab} index={8}>
          {loadingSettings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Spend & Save Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Spend & Save Auto-Withdrawal
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settingsForm.spendSave.enabled}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              spendSave: { ...settingsForm.spendSave, enabled: e.target.checked }
                            })}
                            color="primary"
                          />
                        }
                        label="Enable Auto-Withdrawal"
                        sx={{ mb: 2 }}
                      />
                      
                      {settingsForm.spendSave.enabled && (
                        <>
                          <TextField
                            fullWidth
                            label="Minimum Balance (₦)"
                            type="number"
                            value={settingsForm.spendSave.minimumBalance}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              spendSave: { ...settingsForm.spendSave, minimumBalance: parseFloat(e.target.value) || 0 }
                            })}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Withdrawal Amount (₦) - 0 = withdraw all"
                            type="number"
                            value={settingsForm.spendSave.withdrawalAmount}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              spendSave: { ...settingsForm.spendSave, withdrawalAmount: parseFloat(e.target.value) || 0 }
                            })}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Withdrawal Percentage (%) - 0 = use amount"
                            type="number"
                            value={settingsForm.spendSave.withdrawalPercentage}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              spendSave: { ...settingsForm.spendSave, withdrawalPercentage: parseFloat(e.target.value) || 0 }
                            })}
                            inputProps={{ min: 0, max: 100 }}
                            sx={{ mb: 2 }}
                          />
                          <FormControl fullWidth>
                            <InputLabel>Destination</InputLabel>
                            <Select
                              value={settingsForm.spendSave.destination}
                              label="Destination"
                              onChange={(e) => setSettingsForm({
                                ...settingsForm,
                                spendSave: { ...settingsForm.spendSave, destination: e.target.value }
                              })}
                            >
                              <MenuItem value="flex">Flex Savings</MenuItem>
                              <MenuItem value="main-wallet">Main Wallet</MenuItem>
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Receive & Save Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Receive & Save Auto-Withdrawal
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settingsForm.receiveSave.enabled}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              receiveSave: { ...settingsForm.receiveSave, enabled: e.target.checked }
                            })}
                            color="primary"
                          />
                        }
                        label="Enable Auto-Withdrawal"
                        sx={{ mb: 2 }}
                      />
                      
                      {settingsForm.receiveSave.enabled && (
                        <>
                          <TextField
                            fullWidth
                            label="Minimum Balance (₦)"
                            type="number"
                            value={settingsForm.receiveSave.minimumBalance}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              receiveSave: { ...settingsForm.receiveSave, minimumBalance: parseFloat(e.target.value) || 0 }
                            })}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Withdrawal Amount (₦) - 0 = withdraw all"
                            type="number"
                            value={settingsForm.receiveSave.withdrawalAmount}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              receiveSave: { ...settingsForm.receiveSave, withdrawalAmount: parseFloat(e.target.value) || 0 }
                            })}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Withdrawal Percentage (%) - 0 = use amount"
                            type="number"
                            value={settingsForm.receiveSave.withdrawalPercentage}
                            onChange={(e) => setSettingsForm({
                              ...settingsForm,
                              receiveSave: { ...settingsForm.receiveSave, withdrawalPercentage: parseFloat(e.target.value) || 0 }
                            })}
                            inputProps={{ min: 0, max: 100 }}
                            sx={{ mb: 2 }}
                          />
                          <FormControl fullWidth>
                            <InputLabel>Destination</InputLabel>
                            <Select
                              value={settingsForm.receiveSave.destination}
                              label="Destination"
                              onChange={(e) => setSettingsForm({
                                ...settingsForm,
                                receiveSave: { ...settingsForm.receiveSave, destination: e.target.value }
                              })}
                            >
                              <MenuItem value="flex">Flex Savings</MenuItem>
                              <MenuItem value="main-wallet">Main Wallet</MenuItem>
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={fetchAutoWithdrawalSettings}
                    disabled={savingSettings}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveAutoWithdrawalSettings}
                    disabled={savingSettings}
                    startIcon={savingSettings ? <CircularProgress size={20} /> : <EditIcon />}
                  >
                    {savingSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {[1, 2, 3, 4, 5, 6].map((index) => (
          <TabPanel key={index} value={currentTab} index={index}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Balance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSavings
                    .filter(s => {
                      const types = ['all', 'flex', 'las', 'vault', 'fixed', 'spend-save', 'receive-save'];
                      return s.type === types[index];
                    })
                    .map((saving) => (
                      <TableRow key={saving._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {saving.userId?.fullname || saving.userId?.username || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {saving.userId?.email || ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(getSavingsBalance(saving))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={saving.status} 
                            color={getStatusColor(saving.status)}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(saving.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, saving)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        ))}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewSavings(menuSavings)}>
          <ViewIcon sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleBalanceAdjustment(menuSavings)}>
          <EditIcon sx={{ mr: 1 }} /> Adjust Balance
        </MenuItem>
        <MenuItem onClick={() => handleStatusUpdate(menuSavings)}>
          <EditIcon sx={{ mr: 1 }} /> Update Status
        </MenuItem>
        {menuSavings?.type === 'fixed' && (
          <MenuItem onClick={() => handleMaturityUpdate(menuSavings)}>
            <CalendarIcon sx={{ mr: 1 }} /> Manage Maturity
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleDelete(menuSavings)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Savings Details</DialogTitle>
        <DialogContent>
          {selectedSavings && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">User</Typography>
                  <Typography variant="body1">
                    {selectedSavings.savings?.userId?.fullname || selectedSavings.savings?.userId?.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">
                    {getSavingsTypeLabel(selectedSavings.savings?.type)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Balance</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(getSavingsBalance(selectedSavings.savings))}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={selectedSavings.savings?.status} 
                    color={getStatusColor(selectedSavings.savings?.status)}
                    size="small" 
                  />
                </Grid>
                {selectedSavings.recentTransactions && selectedSavings.recentTransactions.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Recent Transactions
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedSavings.recentTransactions.map((tx) => (
                            <TableRow key={tx._id}>
                              <TableCell>{tx.transactionType}</TableCell>
                              <TableCell>{formatCurrency(tx.amount)}</TableCell>
                              <TableCell>{new Date(tx.createdAt).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Balance Adjustment Dialog */}
      <Dialog open={balanceDialogOpen} onClose={() => setBalanceDialogOpen(false)}>
        <DialogTitle>Adjust Balance</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={balanceForm.action}
                onChange={(e) => setBalanceForm({ ...balanceForm, action: e.target.value })}
                label="Action"
              >
                <MenuItem value="add">Add</MenuItem>
                <MenuItem value="subtract">Subtract</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={balanceForm.amount}
              onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={balanceForm.description}
              onChange={(e) => setBalanceForm({ ...balanceForm, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBalanceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBalanceSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Withdrawal Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => {
        setRejectDialogOpen(false);
        setRejectReason('');
        setSelectedWithdrawal(null);
      }}>
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Amount: {selectedWithdrawal && formatCurrency(selectedWithdrawal.amount)}
            </Typography>
            <TextField
              fullWidth
              label="Rejection Reason"
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setRejectReason('');
            setSelectedWithdrawal(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleRejectWithdrawal} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maturity Update Dialog */}
      <Dialog open={maturityDialogOpen} onClose={() => setMaturityDialogOpen(false)}>
        <DialogTitle>Manage Fixed Savings Maturity</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 400 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={maturityForm.action}
                onChange={(e) => setMaturityForm({ ...maturityForm, action: e.target.value })}
                label="Action"
              >
                <MenuItem value="mature">Mature Now</MenuItem>
                <MenuItem value="extend">Extend Maturity</MenuItem>
              </Select>
            </FormControl>
            {maturityForm.action === 'extend' && (
              <TextField
                fullWidth
                label="New Maturity Date"
                type="date"
                value={maturityForm.newMaturityDate}
                onChange={(e) => setMaturityForm({ ...maturityForm, newMaturityDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaturityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleMaturitySubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Savings Types Tab Panel */}
      <TabPanel value={currentTab} index={9}>
        {loadingTypes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell align="center">ROI (%)</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savingsTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No savings types found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  savingsTypes.map((type) => (
                    <ROICell
                      key={type._id || type.type}
                      type={type}
                      onUpdateROI={handleUpdateROI}
                      onToggleEnabled={handleToggleSavingsType}
                      updatingType={updatingType}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ROI Cell Component
const ROICell = ({ type, onUpdateROI, onToggleEnabled, updatingType }) => {
  const [roiValue, setRoiValue] = React.useState(type.roi || 0);
  const [editingROI, setEditingROI] = React.useState(false);

  React.useEffect(() => {
    setRoiValue(type.roi || 0);
  }, [type.roi]);

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" fontWeight="bold">
          {type.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip label={type.type} size="small" />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {type.description}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {type.displayOrder || 0}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {editingROI ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              type="number"
              size="small"
              value={roiValue}
              onChange={(e) => setRoiValue(e.target.value)}
              inputProps={{ 
                min: 0, 
                max: 100, 
                step: 0.01,
                style: { width: '80px', textAlign: 'center' }
              }}
              onBlur={() => {
                onUpdateROI(type.type, roiValue);
                setEditingROI(false);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onUpdateROI(type.type, roiValue);
                  setEditingROI(false);
                }
              }}
              autoFocus
            />
          </Box>
        ) : (
          <Tooltip title="Click to edit ROI">
            <Typography
              variant="body2"
              onClick={() => setEditingROI(true)}
              sx={{
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              {type.roi || 0}%
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="center">
        <Switch
          checked={type.enabled}
          onChange={(e) => onToggleEnabled(type.type, e.target.checked)}
          disabled={updatingType === type.type}
          color="primary"
        />
      </TableCell>
      <TableCell align="right">
        {updatingType === type.type && (
          <CircularProgress size={20} />
        )}
      </TableCell>
    </TableRow>
  );
};

export default SavingsManagement;

